import { useAtomValue, useSetAtom } from "jotai";
import { useCallback } from "react";
import { widgetMenuOptionsAtom, widgetMenuOptionsByNameAtom } from "@/interface/AppStore";
import { VStack } from "@/shared/ui/Stack";
import { applyValue, setNewOptions } from "../../lib";
import type { ActiveSubMenuState, SetActiveSubMenu } from "../../types";
import { SubMenu } from "../SubMenu/SubMenu";
import { TextAlignMenuContent } from "./TextAlignMenuContent";
import { TextVerticalAlignMenuContent } from "./TextVerticalAlignMenuContent";

interface TextAlignMenuProps {
    activeSubMenu: ActiveSubMenuState;
    setActiveSubMenu: SetActiveSubMenu;
}

export function TextAlignMenu({ activeSubMenu, setActiveSubMenu }: TextAlignMenuProps) {
    const setWidgetMenuOptions = useSetAtom(widgetMenuOptionsAtom);
    const optionsTextHorizontalAlign = useAtomValue(widgetMenuOptionsByNameAtom("textAlign"));
    const optionsTextVerticalAlign = useAtomValue(widgetMenuOptionsByNameAtom("textVerticalAlign"));

    const handleHorizontalAlignButtonClick = useCallback(
        (align: string) => {
            const setNewTextAlignOptions = setNewOptions({
                name: "textAlign",
                setWidgetMenuOptions,
            });

            function handleClose() {
                setActiveSubMenu(null);
            }

            applyValue({
                options: optionsTextHorizontalAlign,
                value: align,
                setNewOptions: setNewTextAlignOptions,
                handleClose,
            });
        },
        [optionsTextHorizontalAlign, setWidgetMenuOptions, setActiveSubMenu],
    );

    const handleVerticalAlignButtonClick = useCallback(
        (align: string) => {
            const setNewTextVerticalAlignOptions = setNewOptions({
                name: "textVerticalAlign",
                setWidgetMenuOptions,
            });

            function handleClose() {
                setActiveSubMenu(null);
            }

            applyValue({
                options: optionsTextVerticalAlign,
                value: align,
                setNewOptions: setNewTextVerticalAlignOptions,
                handleClose,
            });
        },
        [optionsTextVerticalAlign, setWidgetMenuOptions, setActiveSubMenu],
    );
    const showTextVerticalAlignItem = optionsTextVerticalAlign?.length > 0;
    return (
        <SubMenu activeSubMenu={activeSubMenu}>
            <VStack gap={2}>
                <TextAlignMenuContent onTextAlignButtonClick={handleHorizontalAlignButtonClick} />
                {showTextVerticalAlignItem && (
                    <TextVerticalAlignMenuContent
                        onTextAlignButtonClick={handleVerticalAlignButtonClick}
                    />
                )}
            </VStack>
        </SubMenu>
    );
}
